/**
 * Song learning (completion plan P5.5). Music is OPTIONAL: language practice
 * never depends on this page.
 *
 *   1. choose a song   — only rights-cleared pieces are listed by the server
 *   2. meaning/context — per section: context, then words and meaning ONLY as
 *                        the rights grant allows (re-checked in utils/songRights)
 *   3. speak along     — the SAME CoachingSessionView every practice uses, as a
 *                        Development language-practice attempt; the server
 *                        supplies the words through its rights gate
 *   4. pronunciation / rhythm — UNAVAILABLE until LevelUp Media measures audio
 *   5. challenge       — declared, gated (no team-challenge song goal exists)
 *   6. share           — existing per-item publication, only with recording_share
 *
 * Hidden entirely while the server has song learning switched off.
 */
import { useState } from 'react';
import clsx from 'clsx';
import { useGetCapabilitiesQuery } from '../store/api/capabilitiesApi';
import {
  useGetSongCatalogQuery,
  useGetSongQuery,
  type SongSectionView,
  type SongView,
} from '../store/api/songLearningApi';
import { CoachingSessionView } from './CoachingPage';
import { SongDetail, languageLabel } from '../components/song/SongDetail';
import { RIGHTS_BASIS_LABEL } from '../utils/songRights';
import { LoadErrorNotice } from '../components/LoadErrorNotice';

interface Started {
  view: SongView;
  section: SongSectionView;
}

export function SongLearningPage() {
  const { data: caps, isLoading: capsLoading } = useGetCapabilitiesQuery();
  const enabled = caps?.songLearning === true;
  const { data: catalog, isLoading, isError } = useGetSongCatalogQuery(undefined, { skip: !enabled });
  const [ref, setRef] = useState<string | null>(null);
  const {
    data: detail,
    isFetching: detailLoading,
    isError: detailFailed,
    error: detailError,
    refetch: refetchDetail,
  } = useGetSongQuery(ref ?? '', { skip: !enabled || !ref });
  const [started, setStarted] = useState<Started | null>(null);

  if (capsLoading) return null;
  if (!enabled) {
    return (
      <div className="card p-6" data-testid="song-learning-off">
        <h1 className="text-xl font-semibold">Learn with songs</h1>
        <p className="mt-2 text-gray-500">Song practice is not available. Language practice works without it.</p>
      </div>
    );
  }

  if (started) {
    return (
      <CoachingSessionView
        persona={{
          id: 'supportive',
          name: 'Supportive Coach',
          role: 'Language coach',
          description: 'Helps you speak along and use the words in conversation.',
          personality: [],
          difficulty: 'easy',
        }}
        scenario={{
          id: `song:${started.view.ref}:${started.section.id}`,
          title: `${started.view.title} — ${started.section.label}`,
          description: started.section.context,
          category: 'Song practice',
          difficulty: started.view.level,
          duration: '5–10 min',
        }}
        initialMode="development"
        lockMode
        songPractice={{
          request: { songRef: started.view.ref, sectionId: started.section.id },
          language: started.view.language,
        }}
        onEnd={() => setStarted(null)}
      />
    );
  }

  const songs = catalog?.songs ?? [];

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Learn with songs</h1>
        <p className="mt-1 text-gray-500">
          Learn a language through short practice pieces: understand what they mean, practise them section by section, and
          speak along with your coach.
        </p>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading songs…</p>}
      {isError && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
          <p className="text-sm text-amber-900">Song practice is not available right now.</p>
        </div>
      )}
      {catalog && songs.length === 0 && (
        <div className="rounded-lg bg-gray-50 border border-gray-200 p-4" data-testid="song-catalog-empty">
          <p className="text-sm text-gray-700">No songs are available for practice yet.</p>
        </div>
      )}

      {songs.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">1. Choose a song</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" role="radiogroup" aria-label="Song">
            {songs.map((s) => (
              <button
                key={s.ref}
                type="button"
                role="radio"
                aria-checked={ref === s.ref}
                onClick={() => setRef(s.ref)}
                className={clsx(
                  'text-left rounded-lg border p-4 transition-colors',
                  ref === s.ref ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <div className="font-medium text-gray-900">{s.title}</div>
                <p className="mt-1 text-xs text-gray-600">{s.about}</p>
                <p className="mt-2 text-xs text-gray-500">
                  {languageLabel(s.language)} · {s.level} · {s.sectionCount} sections · {RIGHTS_BASIS_LABEL[s.rightsBasis] ?? s.rightsBasis}
                </p>
              </button>
            ))}
          </div>
        </section>
      )}

      {ref && detailLoading && <p className="text-sm text-gray-500">Loading song…</p>}
      {/* A chosen song that cannot be opened says why (404: the rights gate
          no longer offers it; otherwise a failure / busy with Retry) — it
          used to render nothing at all. */}
      {ref && detailFailed && !detailLoading && (
        <LoadErrorNotice
          error={detailError}
          onRetry={() => void refetchDetail()}
          failedMessage="This song could not be loaded."
          notFound={
            <p className="text-sm text-gray-700" data-testid="song-detail-not-available">
              This song is not available for practice.
            </p>
          }
          testIdPrefix="song-detail"
        />
      )}
      {ref && detail && !detailLoading && (
        <SongDetail
          view={detail.song}
          teamChallengesOn={caps?.teamChallenges === true}
          onSpeakAlong={(section) => setStarted({ view: detail.song, section })}
        />
      )}
    </div>
  );
}

export default SongLearningPage;
